import { useEffect, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Typography from '@/components/base/typography'
import { systemListRevealAnimation } from '@/components/sections/animations/system-list'
import Spacer from '@/components/sections/spacer'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types/wp'

type SystemListProps = {
  collection?: {
    title?: string
    text?: string
    image?: WPImage
  }[]
}

const SystemList = ({ collection, ...props }: SystemListProps) => {
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const itemNodesRef = useRef<HTMLElement[]>([])
  const itemBorderNodesRef = useRef<HTMLElement[]>([])
  const imageNodeRef = useRef<HTMLElement | null>(null)
  const imageInnerNodeRef = useRef<HTMLElement | null>(null)

  useGSAP(
    () => {
      const root = rootRef.current
      if (!root) return

      itemNodesRef.current = Array.from(
        root.querySelectorAll('[data-system-item]')
      ) as HTMLElement[]
      itemBorderNodesRef.current = Array.from(
        root.querySelectorAll('[data-system-item-border]')
      ) as HTMLElement[]
      imageNodeRef.current = root.querySelector(
        '[data-system-image]'
      ) as HTMLElement | null
      imageInnerNodeRef.current = root.querySelector(
        '[data-system-image-inner]'
      ) as HTMLElement | null

      const activeItem = itemNodesRef.current[active]
      const imageNode = imageNodeRef.current
      const imageInnerNode = imageInnerNodeRef.current

      if (imageNode && imageInnerNode && activeItem) {
        gsap.set(imageNode, {
          y: activeItem.offsetTop
        })

        gsap.set(imageInnerNode, {
          yPercent: -100 * active
        })
      }

      if (itemBorderNodesRef.current.length > 0) {
        gsap.set(itemBorderNodesRef.current, {
          scaleX: (index: number) => (index === active ? 1 : 0),
          transformOrigin: 'left center'
        })
      }
    },
    {
      scope: rootRef,
      dependencies: [collection]
    }
  )

  useEffect(() => {
    const itemNodes = itemNodesRef.current
    const itemBorderNodes = itemBorderNodesRef.current
    const imageNode = imageNodeRef.current
    const imageInnerNode = imageInnerNodeRef.current
    const activeItem = itemNodes[active]

    if (!activeItem) return

    if (imageNode && imageInnerNode) {
      gsap.to(imageNode, {
        y: activeItem.offsetTop,
        duration: 0.7,
        ease: 'power2.out',
        overwrite: 'auto'
      })

      gsap.to(imageInnerNode, {
        yPercent: -100 * active,
        duration: 0.7,
        ease: 'power2.out',
        overwrite: 'auto'
      })
    }

    if (itemBorderNodes.length > 0) {
      gsap.to(itemBorderNodes, {
        scaleX: (index: number) => (index === active ? 1 : 0),
        transformOrigin: 'left center',
        duration: 0.7,
        ease: 'power2.out',
        overwrite: 'auto'
      })
    }
  }, [active])

  return (
    <Motion animation={systemListRevealAnimation}>
      {(motionRef) => (
        <section ref={motionRef} {...props}>
          <Container>
            <Spacer variant="xxl" />
            <div ref={rootRef} className="relative flex flex-col">
              {collection?.map((item, index) => (
                <Grid
                  key={`system-list-item-${index}`}
                  className={cn(
                    'relative cursor-auto items-center gap-spacing-sm py-spacing-sm md:cursor-pointer',
                    'pl-[80px] md:pl-0'
                  )}
                  data-system-item
                  onPointerEnter={() => {
                    setActive(index)
                  }}
                  data-motion-item
                >
                  <div className="absolute left-0 top-[22px] z-10 block size-[62px] md:hidden">
                    <img
                      src={item.image?.url}
                      alt={item.image?.alt || `System list item ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <span
                    className="pointer-events-none absolute bottom-0 left-0 hidden h-[2px] w-full bg-[rgba(0,0,0,.2)] md:block"
                    data-system-item-border
                    style={{
                      transform: 'scaleX(0)',
                      transformOrigin: 'left center'
                    }}
                  />
                  <span className="pointer-events-none absolute bottom-0 left-0 block h-[1px] w-full bg-[rgba(0,0,0,.2)] md:hidden" />
                  <div className="col-span-2 flex md:col-span-3 lg:col-span-4">
                    {item.title ? (
                      <Typography variant="h5">{item.title}</Typography>
                    ) : null}
                  </div>
                  <div className="col-span-2 col-start-1 flex md:col-span-2 md:col-start-5 lg:col-span-4 lg:col-start-9">
                    {item.text ? (
                      <Typography variant="regular">{item.text}</Typography>
                    ) : null}
                  </div>
                </Grid>
              ))}
              <div
                className="pointer-events-none absolute hidden aspect-square w-[20vw] -translate-x-1/2 overflow-hidden md:left-[48%] md:top-[0%] md:flex lg:left-[38%] lg:top-[-15%]"
                data-system-image
              >
                <div className="relative h-full w-full" data-system-image-inner>
                  {collection?.map((item, index) => (
                    <div
                      key={`system-list-item-${index}`}
                      className={cn('absolute inset-0 aspect-square w-full')}
                      style={{ transform: `translateY(${index * 100}%)` }}
                    >
                      {item.image?.url ? (
                        <img
                          src={item.image.url}
                          alt={
                            item.image.alt || `System list item ${index + 1}`
                          }
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <Spacer variant="xxl" />
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default SystemList
